import React, { useState, useMemo, useEffect } from "react";
import { 
  ClipboardCheck, 
  ChevronRight, 
  ArrowLeft, 
  Save, 
  Check, 
  AlertTriangle, 
  RefreshCw, 
  Minus, 
  Plus, 
  Info,
  PackageCheck,
  ChevronUp,
  ChevronDown
} from "lucide-react";
import { useSupplyConferences } from "../hooks/useSupplyConferences";

export default function SupplyConferenceSubTab({ supplies, availableSectors, onRefreshSupplies }) {
  const {
    selectedSector,
    activeConferenceId,
    counts,
    isSaving,
    error,
    selectSector,
    updateCount,
    saveDraft,
    finalizeConference,
  } = useSupplyConferences();

  // Estados locais da UI
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [syncStatus, setSyncStatus] = useState({ active: false, currentName: "", index: 0, total: 0 });
  const [successScreen, setSuccessScreen] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState(null);

  // Estado para armazenar a ordenação personalizada dos IDs dos insumos
  const [customOrder, setCustomOrder] = useState([]);
  
  // Estado para definir se o item é contado por unidade ou por embalagem
  const [countMode, setCountMode] = useState({}); // { [supplyId]: "unit" | "pack" }

  // Carrega a ordenação salva no localStorage quando o setor muda
  useEffect(() => {
    if (selectedSector) {
      const savedOrder = localStorage.getItem(`supply_conf_order_${selectedSector.toLowerCase()}`);
      if (savedOrder) {
        try {
          setCustomOrder(JSON.parse(savedOrder));
        } catch (e) {
          setCustomOrder([]);
        }
      } else {
        setCustomOrder([]);
      }
    } else {
      setCustomOrder([]);
    }
  }, [selectedSector]);

  // Filtra e ordena insumos pertencentes ao setor selecionado
  const sectorSupplies = useMemo(() => {
    if (!selectedSector) return [];
    
    // Filtra primeiro
    const baseList = supplies.filter(
      (s) => s.sector?.toLowerCase() === selectedSector.toLowerCase()
    );

    if (customOrder.length === 0) return baseList;

    // Ordena de acordo com customOrder
    return [...baseList].sort((a, b) => {
      const idxA = customOrder.indexOf(a.id);
      const idxB = customOrder.indexOf(b.id);
      
      if (idxA === -1 && idxB === -1) return a.name.localeCompare(b.name);
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });
  }, [supplies, selectedSector, customOrder]);

  // Função para mover itens na lista de ordenação
  const moveItem = (index, direction) => {
    const newOrder = sectorSupplies.map(s => s.id);
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    
    // Inverte a posição dos itens
    const temp = newOrder[index];
    newOrder[index] = newOrder[targetIndex];
    newOrder[targetIndex] = temp;
    
    setCustomOrder(newOrder);
    localStorage.setItem(`supply_conf_order_${selectedSector.toLowerCase()}`, JSON.stringify(newOrder));
  };

  // Seção de backup silencioso / tempo de último rascunho salvo
  useEffect(() => {
    if (selectedSector) {
      const localBackupRaw = localStorage.getItem(`supply_conf_backup_${selectedSector.toLowerCase()}`);
      if (localBackupRaw) {
        try {
          const parsed = JSON.parse(localBackupRaw);
          if (parsed.updatedAt) {
            setLastSavedTime(new Date(parsed.updatedAt).toLocaleTimeString());
          }
        } catch (e) {}
      }
    } else {
      setLastSavedTime(null);
    }
  }, [selectedSector, counts]);



  // Trata o clique de salvar rascunho manual
  const handleSaveDraft = async () => {
    try {
      await saveDraft(supplies);
      const now = new Date();
      setLastSavedTime(now.toLocaleTimeString());
      alert("Rascunho salvo com sucesso no PocketBase!");
    } catch (err) {
      alert("Erro ao salvar rascunho: " + err.message);
    }
  };

  // Trata o início da finalização (abre o modal de resumo)
  const handleStartFinalization = () => {
    setShowSummaryModal(true);
  };

  // Executa a finalização e a reconciliação física no banco de dados
  const handleConfirmFinalize = async () => {
    setShowSummaryModal(false);
    setSyncStatus({ active: true, currentName: "Iniciando reconciliação...", index: 0, total: sectorSupplies.length });
    
    try {
      await finalizeConference(supplies, (itemName, idx, tot) => {
        setSyncStatus({ active: true, currentName: itemName, index: idx, total: tot });
      });
      
      // Sucesso!
      setSyncStatus({ active: false, currentName: "", index: 0, total: 0 });
      setSuccessScreen(true);
      if (onRefreshSupplies) {
        await onRefreshSupplies(); // recarrega a tabela de insumos principal
      }
    } catch (err) {
      setSyncStatus({ active: false, currentName: "", index: 0, total: 0 });
      alert("Erro ao finalizar conferência: " + err.message);
    }
  };

  // Agrupamentos e cálculos estatísticos para o Modal de Resumo
  const statsSummary = useMemo(() => {
    let matches = 0;
    let discrepancies = 0;
    let belowMin = 0;
    const itemsList = [];

    sectorSupplies.forEach((s) => {
      const counted = counts[s.id] !== undefined ? counts[s.id] : 0;
      const expected = s.current_quantity || 0;
      const minQty = s.min_quantity || 0;
      const diff = counted - expected;

      if (diff === 0) matches++;
      else discrepancies++;

      if (counted <= minQty) belowMin++;

      itemsList.push({
        name: s.name,
        unit: s.unit || "un",
        expected,
        counted,
        diff,
        belowMin: counted <= minQty
      });
    });

    return { matches, discrepancies, belowMin, itemsList };
  }, [sectorSupplies, counts]);

  // --- TELA DE SUCESSO ---
  if (successScreen) {
    return (
      <div className="flex flex-col items-center justify-center p-6 my-12 text-center max-w-md mx-auto bg-white rounded-2xl shadow-lg border border-emerald-100 animate-in fade-in zoom-in duration-300">
        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center border border-emerald-100 mb-6 text-emerald-600 shadow-inner">
          <PackageCheck size={44} className="animate-bounce" />
        </div>
        <h3 className="text-2xl font-bold text-slate-800">Conferência Concluída!</h3>
        <p className="text-slate-500 mt-2 text-sm leading-relaxed">
          Os saldos físicos dos insumos foram reconciliados e atualizados com sucesso no PocketBase. As discrepâncias foram registradas no relatório.
        </p>
        <button
          onClick={() => setSuccessScreen(false)}
          className="mt-8 w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:shadow-md transition-all active:scale-[0.98]"
        >
          Iniciar Outra Conferência
        </button>
      </div>
    );
  }

  // --- OVERLAY DE PROGRESSO DE RECONCILIAÇÃO ---
  if (syncStatus.active) {
    const percent = Math.round((syncStatus.index / syncStatus.total) * 100) || 0;
    return (
      <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[60] flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full text-center flex flex-col items-center">
          <RefreshCw className="animate-spin text-blue-600 mb-4" size={40} />
          <h4 className="text-lg font-bold text-slate-800">Sincronizando Estoque</h4>
          <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-semibold">
            {syncStatus.index} de {syncStatus.total} ({percent}%)
          </p>
          <div className="w-full bg-slate-100 h-2 rounded-full mt-4 overflow-hidden">
            <div 
              className="bg-blue-600 h-full rounded-full transition-all duration-300"
              style={{ width: `${percent}%` }}
            ></div>
          </div>
          <p className="text-slate-600 text-sm font-medium mt-4 truncate max-w-full italic">
            "{syncStatus.currentName}"
          </p>
          <span className="text-[10px] text-slate-400 mt-6 block">
            Por favor, não feche esta janela...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-hidden w-full relative">
      
      {/* 1. SELEÇÃO INICIAL DE SETOR */}
      {!selectedSector ? (
        <div className="flex-1 p-6 overflow-y-auto max-w-4xl mx-auto w-full flex flex-col justify-center py-12 md:py-16">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-100 shadow-sm">
              <ClipboardCheck size={32} />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Selecione o Setor de Contagem</h2>
            <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
              Selecione o setor para o qual você deseja realizar a conferência física do inventário.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {availableSectors.length === 0 ? (
              <div className="col-span-full bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-center text-sm flex items-center gap-2 justify-center">
                <AlertTriangle size={18} />
                Nenhum setor cadastrado. Cadastre insumos com setores para começar.
              </div>
            ) : (
              availableSectors.map((sector) => (
                <button
                  key={sector}
                  onClick={() => selectSector(sector, supplies)}
                  className="group bg-white hover:bg-blue-50/30 border border-slate-200 hover:border-blue-300 rounded-2xl p-6 text-left shadow-sm hover:shadow transition-all active:scale-[0.98] flex items-center justify-between"
                >
                  <div>
                    <span className="text-xs font-bold text-blue-600 tracking-wider uppercase block mb-1">Setor</span>
                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-blue-900 transition-colors">
                      {sector.toUpperCase()}
                    </h3>
                    <span className="text-xs text-slate-400 mt-2 block">
                      {supplies.filter((s) => s.sector?.toLowerCase() === sector.toLowerCase()).length} Insumos vinculados
                    </span>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-slate-50 group-hover:bg-blue-100 group-hover:text-blue-600 flex items-center justify-center text-slate-400 transition-all">
                    <ChevronRight size={18} />
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        
        // 2. INTERFACE DE CONTAGEM ATIVA
        <div className="flex-1 flex flex-col overflow-hidden h-full">
          
          {/* Header de Ação Superior */}
          <div className="bg-white border-b border-slate-200 p-4 shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (Object.keys(counts).length > 0 && !window.confirm("Sair da conferência ativa? As alterações não salvas serão mantidas localmente, mas não estarão no PocketBase.")) return;
                  selectSector("");
                }}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                title="Voltar aos Setores"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                  Conferindo: <span className="text-blue-600 uppercase">{selectedSector}</span>
                  {activeConferenceId && (
                    <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">RASCUNHO ATIVO</span>
                  )}
                </h3>
                <p className="text-xs text-slate-400">
                  {sectorSupplies.length} insumos listados neste setor
                </p>
              </div>
            </div>


          </div>

          {/* Listagem de Cards de Contagem em Lista */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 pb-28">
            <div className="max-w-5xl mx-auto">
              
              {/* Visualização em Tabela (Desktop/Tablet) */}
              <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50 text-slate-500 text-xs font-bold border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="p-4 w-20 text-center">Ordem</th>
                      <th className="p-4">Insumo</th>
                      <th className="p-4">Categoria</th>
                      <th className="p-4 text-center">Saldos</th>
                      <th className="p-4 text-center w-[300px]">Contagem Física</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {sectorSupplies.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-slate-400">
                          Nenhum insumo encontrado para este setor.
                        </td>
                      </tr>
                    ) : (
                      sectorSupplies.map((s, index) => {
                        const counted = counts[s.id] !== undefined ? counts[s.id] : 0;
                        const expected = s.current_quantity || 0;
                        const isBelowMin = counted <= (s.min_quantity || 0);
                        const hasDiscrepancy = counted !== expected;

                        return (
                          <tr 
                            key={s.id} 
                            className={`hover:bg-slate-50 transition-colors ${
                              hasDiscrepancy ? "bg-amber-50/20" : ""
                            }`}
                          >
                            {/* Reordenação */}
                            <td className="p-4 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveItem(index, "up")}
                                  disabled={index === 0}
                                  className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:pointer-events-none rounded hover:bg-slate-200 transition-colors"
                                  title="Subir item"
                                >
                                  <ChevronUp size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveItem(index, "down")}
                                  disabled={index === sectorSupplies.length - 1}
                                  className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:pointer-events-none rounded hover:bg-slate-200 transition-colors"
                                  title="Descer item"
                                >
                                  <ChevronDown size={16} />
                                </button>
                              </div>
                            </td>

                            {/* Insumo */}
                            <td className="p-4">
                              <div className="font-bold text-slate-800 text-sm leading-snug">{s.name}</div>
                              <div className="flex items-center gap-2 mt-1">
                                {hasDiscrepancy && (
                                  <span className="text-[9px] font-black bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded uppercase">
                                    Diferença: {counted - expected > 0 ? `+${counted - expected}` : counted - expected}
                                  </span>
                                )}
                                {isBelowMin && (
                                  <span className="text-[9px] font-black bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded uppercase">
                                    Abaixo Mín
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Categoria */}
                            <td className="p-4">
                              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded uppercase">
                                {s.category || "Sem categoria"}
                              </span>
                            </td>

                            {/* Saldos */}
                            <td className="p-4 text-center whitespace-nowrap">
                              <div className="text-xs text-slate-500">
                                Esperado: <strong className="text-slate-700">{expected} {s.unit || "un"}</strong>
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Alerta Mín: {s.min_quantity || 0} {s.unit || "un"}
                              </div>
                            </td>

                            {/* Controle de Quantidade */}
                            <td className="p-4">
                              {s.unidades_por_compra > 1 ? (
                                <div className="flex flex-col items-center gap-1.5 max-w-[280px] mx-auto">
                                  <div className="flex items-center gap-2 w-full">
                                    {/* Input Embalagens */}
                                    <div className="flex-1 flex items-center border border-slate-200 rounded bg-white px-2 py-1 shadow-sm">
                                      <input
                                        type="number"
                                        min="0"
                                        value={counts[s.id] === undefined ? "" : Math.floor(counted / s.unidades_por_compra)}
                                        onChange={(e) => {
                                          const val = e.target.value === "" ? 0 : Number(e.target.value);
                                          const currentUnit = counts[s.id] !== undefined ? (counts[s.id] % s.unidades_por_compra) : 0;
                                          updateCount(s.id, val * s.unidades_por_compra + currentUnit);
                                        }}
                                        className="w-full text-center font-mono font-bold text-slate-800 text-sm outline-none"
                                        placeholder="0"
                                      />
                                      <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">cx</span>
                                    </div>
                                    <span className="text-slate-400 font-bold text-xs">+</span>
                                    {/* Input Unidades */}
                                    <div className="flex-1 flex items-center border border-slate-200 rounded bg-white px-2 py-1 shadow-sm">
                                      <input
                                        type="number"
                                        min="0"
                                        value={counts[s.id] === undefined ? "" : counted % s.unidades_por_compra}
                                        onChange={(e) => {
                                          const val = e.target.value === "" ? 0 : Number(e.target.value);
                                          const currentPack = counts[s.id] !== undefined ? Math.floor(counts[s.id] / s.unidades_por_compra) : 0;
                                          updateCount(s.id, currentPack * s.unidades_por_compra + val);
                                        }}
                                        className="w-full text-center font-mono font-bold text-slate-800 text-sm outline-none"
                                        placeholder="0"
                                      />
                                      <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">un</span>
                                    </div>
                                  </div>
                                  {counted > 0 && (
                                    <span className="text-[10px] font-semibold text-blue-600">
                                      Total: {counted} {s.unit || "un"}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center justify-between gap-1.5 bg-slate-50 p-1.5 rounded-lg border border-slate-100 max-w-[280px] mx-auto">
                                  <button
                                    type="button"
                                    onClick={() => updateCount(s.id, (c) => Math.max(0, c - 5))}
                                    className="w-7 h-7 rounded bg-white border border-slate-200 active:bg-slate-100 text-slate-500 font-bold text-[10px] flex items-center justify-center transition-colors shadow-sm select-none"
                                  >
                                    -5
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateCount(s.id, (c) => Math.max(0, c - 1))}
                                    className="w-7 h-7 rounded bg-white border border-slate-200 active:bg-slate-100 text-slate-500 flex items-center justify-center transition-colors shadow-sm select-none"
                                  >
                                    <Minus size={12} />
                                  </button>

                                  <div className="flex-1 flex items-center justify-center relative min-w-[60px]">
                                    <input
                                      type="number"
                                      min="0"
                                      step="any"
                                      value={counts[s.id] !== undefined ? counts[s.id] : ""}
                                      onChange={(e) => {
                                        const val = e.target.value === "" ? 0 : Number(e.target.value);
                                        updateCount(s.id, val);
                                      }}
                                      className="w-full h-7 bg-white border border-slate-200 rounded text-center font-mono font-bold text-slate-800 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                                      placeholder="0"
                                    />
                                  </div>

                                  <button
                                    type="button"
                                    onClick={() => updateCount(s.id, (c) => c + 1)}
                                    className="w-7 h-7 rounded bg-white border border-slate-200 active:bg-slate-100 text-slate-500 flex items-center justify-center transition-colors shadow-sm select-none"
                                  >
                                    <Plus size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateCount(s.id, (c) => c + 5)}
                                    className="w-7 h-7 rounded bg-white border border-slate-200 active:bg-slate-100 text-slate-500 font-bold text-[10px] flex items-center justify-center transition-colors shadow-sm select-none"
                                  >
                                    +5
                                  </button>
                                </div>
                              )}
                            </td>


                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Visualização em Cards (Mobile) */}
              <div className="block md:hidden space-y-3">
                {sectorSupplies.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 bg-white border border-slate-200 rounded-xl">
                    Nenhum insumo encontrado para este setor.
                  </div>
                ) : (
                  sectorSupplies.map((s, index) => {
                    const counted = counts[s.id] !== undefined ? counts[s.id] : 0;
                    const expected = s.current_quantity || 0;
                    const isBelowMin = counted <= (s.min_quantity || 0);
                    const hasDiscrepancy = counted !== expected;

                    return (
                      <div 
                        key={s.id}
                        className={`p-4 bg-white border rounded-xl shadow-sm transition-all flex flex-col gap-3 ${
                          hasDiscrepancy ? "border-amber-200 bg-amber-50/10" : "border-slate-200"
                        }`}
                      >
                        {/* Top row: Name & Category */}
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <div className="font-bold text-slate-800 text-sm">{s.name}</div>
                            <div className="text-[10px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded uppercase inline-block mt-1">
                              {s.category || "Sem categoria"}
                            </div>
                          </div>
                          
                          {/* Reorder actions */}
                          <div className="flex items-center gap-1 border border-slate-100 bg-slate-50 p-1 rounded-lg shrink-0">
                            <button
                              type="button"
                              onClick={() => moveItem(index, "up")}
                              disabled={index === 0}
                              className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 rounded hover:bg-slate-200 transition"
                              title="Subir"
                            >
                              <ChevronUp size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveItem(index, "down")}
                              disabled={index === sectorSupplies.length - 1}
                              className="p-1 text-slate-400 hover:text-blue-600 disabled:opacity-30 rounded hover:bg-slate-200 transition"
                              title="Descer"
                            >
                              <ChevronDown size={16} />
                            </button>
                          </div>
                        </div>

                        {/* Badges row */}
                        <div className="flex flex-wrap gap-1">
                          {hasDiscrepancy && (
                            <span className="text-[9px] font-black bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded uppercase">
                              Diferença: {counted - expected > 0 ? `+${counted - expected}` : counted - expected}
                            </span>
                          )}
                          {isBelowMin && (
                            <span className="text-[9px] font-black bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded uppercase">
                              Abaixo Mín
                            </span>
                          )}
                        </div>

                        {/* Details / Stock info */}
                        <div className="flex justify-between items-center text-xs bg-slate-50/50 p-2 rounded-lg border border-slate-100">
                          <div>
                            <span className="text-slate-400">Esperado: </span>
                            <strong className="text-slate-700">{expected} {s.unit || "un"}</strong>
                          </div>
                          <div className="text-[10px] text-slate-400">
                            Alerta Mín: {s.min_quantity || 0} {s.unit || "un"}
                          </div>
                        </div>

                        {/* Control row */}
                        <div className="flex flex-col gap-2 mt-1">
                          {s.unidades_por_compra > 1 ? (
                            <div className="flex flex-col gap-1 w-full max-w-[280px]">
                              <div className="flex items-center gap-2">
                                {/* Input Embalagens */}
                                <div className="flex-1 flex items-center border border-slate-200 rounded-xl bg-white px-3 py-2 shadow-sm">
                                  <input
                                    type="number"
                                    min="0"
                                    value={counts[s.id] === undefined ? "" : Math.floor(counted / s.unidades_por_compra)}
                                    onChange={(e) => {
                                      const val = e.target.value === "" ? 0 : Number(e.target.value);
                                      const currentUnit = counts[s.id] !== undefined ? (counts[s.id] % s.unidades_por_compra) : 0;
                                      updateCount(s.id, val * s.unidades_por_compra + currentUnit);
                                    }}
                                    className="w-full text-center font-mono font-bold text-slate-800 text-sm outline-none"
                                    placeholder="0"
                                  />
                                  <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">cx</span>
                                </div>
                                <span className="text-slate-400 font-bold text-xs">+</span>
                                {/* Input Unidades */}
                                <div className="flex-1 flex items-center border border-slate-200 rounded-xl bg-white px-3 py-2 shadow-sm">
                                  <input
                                    type="number"
                                    min="0"
                                    value={counts[s.id] === undefined ? "" : counted % s.unidades_por_compra}
                                    onChange={(e) => {
                                      const val = e.target.value === "" ? 0 : Number(e.target.value);
                                      const currentPack = counts[s.id] !== undefined ? Math.floor(counts[s.id] / s.unidades_por_compra) : 0;
                                      updateCount(s.id, currentPack * s.unidades_por_compra + val);
                                    }}
                                    className="w-full text-center font-mono font-bold text-slate-800 text-sm outline-none"
                                    placeholder="0"
                                  />
                                  <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">un</span>
                                </div>


                              </div>
                              {counted > 0 && (
                                <span className="text-[10px] font-semibold text-blue-600 mt-0.5">
                                  Total: {counted} {s.unit || "un"}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200 flex-1 justify-between max-w-[200px]">
                                <button
                                  type="button"
                                  onClick={() => updateCount(s.id, (c) => Math.max(0, c - 5))}
                                  className="w-8 h-8 rounded bg-white border border-slate-200 active:bg-slate-200 text-slate-500 font-bold text-xs flex items-center justify-center transition-colors shadow-sm select-none"
                                >
                                  -5
                                </button>
                                <button
                                  type="button"
                                  onClick={() => updateCount(s.id, (c) => Math.max(0, c - 1))}
                                  className="w-8 h-8 rounded bg-white border border-slate-200 active:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors shadow-sm select-none"
                                >
                                  <Minus size={12} />
                                </button>

                                <input
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={counts[s.id] !== undefined ? counts[s.id] : ""}
                                  onChange={(e) => {
                                    const val = e.target.value === "" ? 0 : Number(e.target.value);
                                    updateCount(s.id, val);
                                  }}
                                  className="w-12 h-8 bg-white border border-slate-200 rounded text-center font-mono font-bold text-slate-800 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                                  placeholder="0"
                                />

                                <button
                                  type="button"
                                  onClick={() => updateCount(s.id, (c) => c + 1)}
                                  className="w-8 h-8 rounded bg-white border border-slate-200 active:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors shadow-sm select-none"
                                >
                                  <Plus size={12} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => updateCount(s.id, (c) => c + 5)}
                                  className="w-8 h-8 rounded bg-white border border-slate-200 active:bg-slate-200 text-slate-500 font-bold text-xs flex items-center justify-center transition-colors shadow-sm select-none"
                                >
                                  +5
                                </button>
                              </div>


                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              {/* Spacer para evitar sobreposição do rodapé fixo */}
              <div className="h-32" />
            </div>
          </div>

          {/* Sticky Action Bar (Rodapé Fixo) */}
          <div className="absolute bottom-0 left-0 w-full bg-white border-t border-slate-200 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] flex items-center justify-between gap-4 z-40">
            <div className="flex flex-col text-left shrink-0">
              <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                Backup Silencioso
              </span>
              <span className="text-xs font-semibold text-slate-600 flex items-center gap-1 mt-0.5">
                <Info size={12} className="text-blue-500" />
                {lastSavedTime ? `Modificado às: ${lastSavedTime}` : "Sem alterações salvas"}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2.5 border border-slate-200 hover:border-slate-300 text-slate-600 hover:bg-slate-50 rounded-xl font-bold text-sm shadow-sm transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {isSaving ? (
                  <RefreshCw className="animate-spin" size={16} />
                ) : (
                  <Save size={16} />
                )}
                <span className="hidden sm:inline">Salvar Rascunho</span>
                <span className="sm:hidden">Salvar</span>
              </button>
              <button
                type="button"
                onClick={handleStartFinalization}
                disabled={isSaving || sectorSupplies.length === 0}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
              >
                <Check size={16} />
                Finalizar Contagem
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. MODAL DE RESUMO / CONFIRMAÇÃO DE RECONCILIAÇÃO */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[85vh] animate-in fade-in zoom-in duration-200 overflow-hidden">
            
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center gap-3 shrink-0 bg-slate-50">
              <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center border border-blue-100">
                <ClipboardCheck size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Reconciliação de Insumos</h3>
                <p className="text-xs text-slate-400">Resumo do balanço geral antes de salvar no estoque físico</p>
              </div>
            </div>

            {/* Conteúdo do Balanço */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {/* Cards de Métricas */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Confirmados</span>
                  <span className="text-xl font-bold text-emerald-600 block mt-1">{statsSummary.matches}</span>
                </div>
                <div className="bg-amber-50/50 rounded-xl p-3 text-center border border-amber-100">
                  <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider block">Ajustados</span>
                  <span className="text-xl font-bold text-amber-600 block mt-1">{statsSummary.discrepancies}</span>
                </div>
                <div className="bg-red-50/50 rounded-xl p-3 text-center border border-red-100">
                  <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider block">Críticos (Mín)</span>
                  <span className="text-xl font-bold text-red-600 block mt-1">{statsSummary.belowMin}</span>
                </div>
              </div>

              {/* Tabela de Discrepâncias detalhada */}
              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Balanço por Item</h4>
                <div className="border border-slate-100 rounded-xl overflow-hidden max-h-[250px] overflow-y-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-slate-500 font-bold sticky top-0 border-b border-slate-100">
                      <tr>
                        <th className="p-3">Insumo</th>
                        <th className="p-3 text-right">Contado</th>
                        <th className="p-3 text-right">Esperado</th>
                        <th className="p-3 text-center">Ajuste</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {statsSummary.itemsList.map((item) => (
                        <tr key={item.name} className="hover:bg-slate-50">
                          <td className="p-3 font-semibold text-slate-700">
                            {item.name}
                            {item.belowMin && (
                              <span className="text-[8px] bg-red-100 text-red-800 px-1 rounded ml-1 font-bold">ABAIXO MÍN</span>
                            )}
                          </td>
                          <td className="p-3 text-right font-mono font-bold text-slate-800">
                            {item.counted} <span className="text-[10px] font-normal text-slate-400">{item.unit}</span>
                          </td>
                          <td className="p-3 text-right font-mono text-slate-500">
                            {item.expected} <span className="text-[10px] font-normal text-slate-400">{item.unit}</span>
                          </td>
                          <td className="p-3 text-center">
                            {item.diff === 0 ? (
                              <span className="text-emerald-600 font-bold">-</span>
                            ) : item.diff > 0 ? (
                              <span className="bg-emerald-50 text-emerald-700 font-bold px-1.5 py-0.5 rounded font-mono">
                                +{item.diff}
                              </span>
                            ) : (
                              <span className="bg-red-50 text-red-700 font-bold px-1.5 py-0.5 rounded font-mono">
                                {item.diff}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Alerta de perigo fail-safe */}
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl text-xs leading-relaxed flex gap-3">
                <AlertTriangle size={18} className="shrink-0 text-amber-600" />
                <p>
                  <strong>ATENÇÃO:</strong> Ao prosseguir, o saldo atual de todos os insumos selecionados será substituído pela quantidade contada fisicamente. Esta ação causará impactos diretos na gestão de compras e receitas.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-100 shrink-0 bg-slate-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowSummaryModal(false)}
                className="px-5 py-2.5 font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-xl transition-colors text-sm"
              >
                Voltar à Edição
              </button>
              <button
                type="button"
                onClick={handleConfirmFinalize}
                className="px-6 py-2.5 font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 rounded-xl transition-all shadow-md active:scale-[0.98] text-sm"
              >
                Confirmar e Sincronizar Estoque
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
